// Queue prompts and deliver each one encrypted once its turn's worker has opened a segment
// (D82): the bubble shows at once, the turn is created on the server, and when the turn names
// its segment the prompt goes out signed under that segment's key — with this device's recent
// history when the key is self-managed. Several prompts may wait; they leave one at a time.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ConversationState } from "../conversation/model";
import { messages } from "../i18n";
import type { ConversationHandle } from "../conversation/useConversation";
import { createTurn, type Turn, turnsQuery, type TurnBody } from "./api";
import { historyPack, nextToSend, type PendingPrompt, promptEnvelope } from "./model";

export interface ChatSender {
  pending: PendingPrompt[];
  submit: (text: string, body: TurnBody) => Promise<void>;
}

let counter = 0;

export function useChatSender(conversationId: string, state: ConversationState, turns: Turn[], send: ConversationHandle["send"], selfManaged: boolean): ChatSender {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingPrompt[]>([]);
  const busy = useRef(false);

  const patch = useCallback((localId: string, change: Partial<PendingPrompt>) => {
    setPending((list) => list.map((p) => (p.localId === localId ? { ...p, ...change } : p)));
  }, []);

  const submit = useCallback(
    async (text: string, body: TurnBody) => {
      counter += 1;
      const localId = `local-${counter}`;
      setPending((list) => [...list, { localId, text, state: "creating" }]);
      try {
        const created = await createTurn(conversationId, body);
        patch(localId, { turnId: created.turn_id, state: "waiting" });
      } catch (e) {
        patch(localId, { state: "failed", error: `${messages().chat.turnFailed}: ${e instanceof Error ? e.message : String(e)}` });
      }
      void queryClient.invalidateQueries({ queryKey: turnsQuery(conversationId).queryKey });
    },
    [conversationId, patch, queryClient],
  );

  useEffect(() => {
    if (busy.current) return;
    const next = nextToSend(pending, turns, state);
    if (!next) return;
    const { prompt, segmentId } = next;
    busy.current = true;
    const history = selfManaged ? historyPack(state.events) : undefined;
    const deliver = async () => {
      patch(prompt.localId, { state: "sending" });
      try {
        await send({ eventType: "instruction", segmentId, text: promptEnvelope(prompt.turnId!, prompt.text, history) });
        patch(prompt.localId, { state: "sent" });
      } catch (e) {
        patch(prompt.localId, { state: "failed", error: e instanceof Error ? e.message : String(e) });
      } finally {
        busy.current = false;
        setPending((list) => [...list]); // look again: the next prompt may be ready
      }
    };
    void deliver();
  }, [state, turns, pending, send, selfManaged, patch]);

  return { pending, submit };
}
